import { fontName as fontName_1 } from './fontName.locstring.json';
import loc from 'owa-localize';
import { callFormatApi } from 'owa-editor/lib/utils/callFormatApi';
import PickerInputButtonBase from '../PickerInputButtonBase';
import { FontPickerItem, FONT_NAME_MENU_ITEMS } from 'owa-format-options';
import setLocalFontList from 'owa-editor-ribbon-secondary-ui/lib/actions/setLocalFontList';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import addFontMru from 'owa-editor-ribbon-secondary-ui/lib/actions/addFontMru';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getStore } from 'owa-editor-ribbon-secondary-ui/lib/store/store';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';

const DEFAULT_FONT_NAME = 'Calibri';

interface ChromeFontItem {
    postscriptName: string;
    fullName: string;
    family: string;
    style: string;
}

interface ChromeFonts {
    query: () => Promise<ChromeFontItem[]>;
}

interface ChromeNavigator extends Navigator {
    fonts: ChromeFonts;
}

function isChromeNavigator(navigator: Navigator): navigator is ChromeNavigator {
    return !!(<ChromeNavigator>navigator).fonts;
}

export default class FontNameWithInput extends PickerInputButtonBase {
    constructor() {
        super(
            'fontName',
            loc(fontName_1),
            getStore().fontName,
            120 /*inputWidth*/,
            256 /*maxLength*/,
            true /*autoComplete*/,
            itemStyle
        );
    }

    updatePickerItems = async (targetWindow: Window) => {
        if (!getStore().isLocalFontListLoaded) {
            const navigator = (targetWindow || window).navigator;
            const items =
                isHostAppFeatureEnabled('native') &&
                isFeatureEnabled('mon-cmp-localFonts') &&
                isChromeNavigator(navigator) &&
                (await this.getLocalFonts(navigator));
            setLocalFontList(items);
        }
    };

    onChange = (value: string, isSubmit: boolean) => {
        callFormatApi(this.editorViewState, 'setFontName', [value]);

        if (isSubmit) {
            addFontMru(getStore().fontName, value);
        }
    };

    normalizeValue = (value: string) => {
        const normalizedValue = getStore().fontName.items.filter(v => v.displayValue == value)[0];
        return normalizedValue?.submitValue || value;
    };

    getCurrentValue = () => {
        return this.formatViewState.fontName || getDefaultFontName();
    };

    protected getPickerStyle = (displayValue: string): React.CSSProperties => {
        const fontFamily = this.normalizeValue(displayValue);
        return fontFamily
            ? {
                  fontFamily,
              }
            : null;
    };

    private async getLocalFonts(navigator: ChromeNavigator): Promise<FontPickerItem[]> {
        try {
            const fontList: Record<string, string> = {};
            const chromeFonts = await navigator.fonts.query();
            chromeFonts.forEach(font => {
                const fontName = fontList[font.family];

                // For a group of fonts with the same family, use their shortest name as font name
                if (!fontName || fontName.length > font.fullName.length) {
                    fontList[font.family] = font.fullName;
                }
            });

            return Object.keys(fontList).map(family => {
                const fullName = fontList[family];
                const staticItem = FONT_NAME_MENU_ITEMS.filter(
                    item => item.displayValue == fullName
                )[0];

                return (
                    staticItem || {
                        displayValue: fullName,
                        selectValue: fullName,
                        submitValue: family,
                    }
                );
            });
        } catch {
            return null;
        }
    }
}

function getDefaultFontName() {
    const userOption = getUserConfiguration().UserOptions;
    return userOption?.ComposeFontName || DEFAULT_FONT_NAME;
}

function itemStyle(item: FontPickerItem) {
    return {
        fontFamily: item.submitValue,
    };
}
